import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilters, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { selectOutputVersionOptions } from '@/redux/selectors/orderPlacementSelectors';
import { selectActiveTitleId, selectIsActiveTitleLoading } from '@/redux/selectors/titleV2Selectors';
import { useQuery } from '@/components/_hooks/useQuery';
import TableContainer from '@/components/_common/Table/TableContainer';
import Spinner from '@/components/_common/Spinner';
import { Grid } from '@mui/material';
import { selectTitleIsSeries } from '@/redux/selectors/titleSelectors';

interface TitleDetailRelatedJobsV2Props {
  lookupId: number;
  isActiveTab: boolean;
}

const TitleDetailRelatedJobsV2 = ({ lookupId, isActiveTab }: TitleDetailRelatedJobsV2Props) => {
  const dispatch = useAppDispatch();
  const currentTable = useAppSelector(selectCurrentTableKey);
  const query = useQuery();
  const versionId = query.get('version_id');
  const isSeries = useAppSelector(selectTitleIsSeries);
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const outputVersionOptions = useAppSelector(selectOutputVersionOptions);
  const versions = outputVersionOptions[lookupId] || [];
  const titleLoading = useAppSelector(selectIsActiveTitleLoading);

  useEffect(() => {
    if (titleLoading || !isActiveTab) return;
    dispatch(setCurrentTable('titleDetailJobs'));
    const filters: Record<string, string[] | undefined> = {
      title_hid_filter: undefined,
      version_hid_filter: undefined
    };

    if (isSeries || !versionId || versionId === 'all') {
      filters.title_hid_filter = [`E${activeTitleId}`];
    } else {
      const version = versions.find((version) => String(version.id) === versionId);
      filters.version_hid_filter = version?.hid ? [version.hid] : undefined;
    }

    dispatch(clearFilters(undefined));
    dispatch(applyFilters({ filters }));
  }, [versionId, activeTitleId, titleLoading, isActiveTab]);

  return !titleLoading && currentTable === 'titleDetailJobs' ? (
    <StyledTableContainer
      onRowClick={(_, job) => {
        window.open(`/jobs/${job.id}`, '_blank');
      }}
      simplified
    />
  ) : (
    <Grid item sx={{ p: 6 }}>
      <Spinner contained />
    </Grid>
  );
};

export default TitleDetailRelatedJobsV2;

const StyledTableContainer = styled(TableContainer)`
  .MuiTableHead-root {
    background: ${({ theme }) => theme.palette.background.paper};
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }

  #overflow {
    background: ${({ theme }) => theme.palette.background.paper};

    > span:nth-of-type(2) {
      background: ${({ theme }) => `linear-gradient(to left, ${theme.palette.background.paper}, transparent 75%)`};
    }
  }
`;
